import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import {
  BciXlsUploadService,
  BCIUploadResponse,
} from '../../services/bci-xls-upload.service';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-manual-xls-bci',
  templateUrl: './manual-xls-bci.component.html',
  styleUrls: ['./manual-xls-bci.component.sass'],
})
export class ManualXlsBciComponent implements OnInit {
  @ViewChild('fileInput') fileInput!: ElementRef;

  selectedAccount: string = '';
  accounts: string[] = ['AGUACATE', 'PARRA'];
  isLoading: boolean = false;
  isDragging: boolean = false;
  selectedFileName: string | null = null;
  excelRecordCount: number | null = null; // New property to store the record count
  maxFileSize: number = 10 * 1024 * 1024; // 10MB
  validFileTypes: string[] = [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ];
  uploadResponse: BCIUploadResponse | null = null;

  get canSubmit(): boolean {
    return (
      Boolean(this.selectedAccount) &&
      Boolean(this.selectedFileName) &&
      !this.isLoading
    );
  }

  constructor(
    private bciUploadService: BciXlsUploadService,
    private snackBar: MatSnackBar,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.bciUploadService.loading$.subscribe((loading) => {
      this.isLoading = loading;
    });
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    if (!this.selectedAccount) {
      this.showMessage(
        'Por favor, selecciona una cuenta antes de subir un archivo',
        'error',
      );
      return;
    }

    const files = event.dataTransfer?.files;
    if (files?.length) {
      if (files.length > 1) {
        this.showMessage('Solo puedes cargar un archivo a la vez', 'error');
        return;
      }
      this.handleFileSelection(files[0]);
    }
  }

  onFileSelected(event: Event): void {
    if (!this.selectedAccount) {
      this.showMessage(
        'Por favor, selecciona una cuenta antes de subir un archivo',
        'error',
      );
      return;
    }

    const input = event.target as HTMLInputElement;
    const files = input.files;

    if (files?.length) {
      if (files.length > 1) {
        this.showMessage('Solo puedes cargar un archivo a la vez', 'error');
        this.clearFile();
        return;
      }
      this.handleFileSelection(files[0]);
    }
  }

  private handleFileSelection(file: File): void {
    if (this.validateFile(file)) {
      this.selectedFileName = file.name;
      this.readExcelFile(file); // Call the new method to read the file
    } else {
      this.clearFile();
    }
  }

  private readExcelFile(file: File): void {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        // Convertir la hoja a JSON, manteniendo las filas vacías
        const json = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: '',
        });

        // Filtrar las filas que no tienen datos.
        // Una fila "vacía" será un array con todos los elementos vacíos ("").
        const nonEmptyRows = json.filter(
          (row) =>
            Array.isArray(row) &&
            row.some((cell) => String(cell).trim() !== ''),
        );

        // Restar 1 para la fila de la cabecera
        this.excelRecordCount =
          nonEmptyRows.length > 0 ? nonEmptyRows.length - 1 : 0;
      } catch (error) {
        this.showMessage('Error al leer el archivo Excel', 'error');
        this.clearFile();
      }
    };
    reader.readAsArrayBuffer(file);
  }

  private validateFile(file: File): boolean {
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.xls') && !fileName.endsWith('.xlsx')) {
      this.showMessage(
        'Solo se permiten archivos Excel (.xls o .xlsx)',
        'error',
      );
      return false;
    }
    if (file.size > this.maxFileSize) {
      this.showMessage('El archivo no debe superar los 10MB', 'error');
      return false;
    }
    if (
      file.type &&
      !this.validFileTypes.includes(file.type) &&
      file.type !== ''
    ) {
      this.showMessage(
        'El archivo debe ser un documento de Excel válido',
        'error',
      );
      return false;
    }
    return true;
  }

  private showMessage(
    message: string,
    type: 'error' | 'success' | 'info' = 'error',
  ): void {
    this.snackBar.open(message, 'Cerrar', {
      duration: 5000,
      panelClass: [`mat-snack-bar-${type}`],
    });
  }

  onSubmit(): void {
    if (!this.canSubmit) {
      this.showMessage('Por favor, selecciona una cuenta y un archivo', 'info');
      return;
    }

    const fileInput: HTMLInputElement = this.fileInput.nativeElement;
    const file = fileInput.files?.[0];

    if (!file || !this.selectedAccount) {
      return;
    }

    this.uploadResponse = null;
    this.bciUploadService.uploadXlsFile(file, this.selectedAccount).subscribe({
      next: (response) => {
        this.uploadResponse = response;
        this.showMessage(
          'El archivo ha sido procesado correctamente',
          'success',
        );
      },
      error: (error: any) => {
        this.showMessage(
          error.message || 'Error al procesar el archivo',
          'error',
        );
      },
      complete: () => {
        // Optional: navigation or other actions
      },
    });
  }

  clearFile(): void {
    this.selectedFileName = null;
    this.uploadResponse = null;
    this.excelRecordCount = null; // Reset the record count
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }
}
